import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Box, LinearProgress } from "@mui/material";
import Flex from "@/components/Flex";
import SearchInput from "@/components/SearchInput";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import {
  ATTENDANCE_TILE,
  ATTENDANCE_TILE_COLORS,
  ATTENDANCE_TILE_LABEL,
  SESSION_NOTE_LABEL,
} from "../group-visit-constant";
import {
  AttendanceStatus,
  AttendanceTally,
  SessionParticipant,
} from "../group-visit-types";
import AttendanceRow from "./AttendanceRow";
import AttendanceTallyTile from "./AttendanceTallyTile";

export type AttendancePanelProps = {
  participants: SessionParticipant[];
  tally: AttendanceTally[];
  searchKey: string;
  onSearch: (value: string) => void;
  onStatusChange: (uuid: string, status: AttendanceStatus) => void;
  onMarkAllPresent: () => void;
  onUnmarkAll: () => void;
  /** Everyone is marked present, so the toggle unmarks instead. */
  isAllPresent: boolean;
  /** Individual notes finished out of those required. */
  completedNotes: number;
  requiredNotes: number;
  /** Participant whose individual note is open, if any. */
  selectedUuid?: string;
  onSelect: (uuid: string) => void;
  /** Opens a participant's signed visit note; omitted while the session is open. */
  onShowSummary?: (uuid: string) => void;
  disabled?: boolean;
};

const FULL_PERCENT = 100;

/**
 * Attendance rail — the tally, the roster and the note completion tracker.
 *
 * @author Rajkumar Rathod
 */
const AttendancePanel = ({
  participants,
  tally,
  searchKey,
  onSearch,
  onStatusChange,
  onMarkAllPresent,
  onUnmarkAll,
  isAllPresent,
  completedNotes,
  requiredNotes,
  selectedUuid,
  onSelect,
  onShowSummary,
  disabled,
}: AttendancePanelProps) => {
  const totalParticipants =
    tally.find((item) => item.id === ATTENDANCE_TILE.TOTAL)?.count ?? 0;
  const progress = requiredNotes
    ? (completedNotes / requiredNotes) * FULL_PERCENT
    : 0;

  return (
    <Flex flexDirection="column" height="100%" sx={{ overflow: "hidden" }}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ flexShrink: 0, px: 1.5, pt: 1.5 }}
      >
        <Text
          {...CHART_TYPOGRAPHY.title6}
          fontWeight={CHART_TYPOGRAPHY.weight.medium}
          c={CHART_COLORS.textDarker}
        >
          {SESSION_NOTE_LABEL.attendance}
        </Text>

        <StyledButtonV2
          label={
            isAllPresent
              ? SESSION_NOTE_LABEL.unmarkAll
              : SESSION_NOTE_LABEL.markAllPresent
          }
          variant="text"
          disabled={disabled}
          onClick={isAllPresent ? onUnmarkAll : onMarkAllPresent}
          sIcon={
            isAllPresent ? (
              <CloseIcon sx={{ fontSize: CHART_DIMENSIONS.iconSm }} />
            ) : (
              <CheckIcon sx={{ fontSize: CHART_DIMENSIONS.iconSm }} />
            )
          }
        />
      </Flex>

      <Flex gap={1} sx={{ flexShrink: 0, px: 1.5, pt: 1.5 }}>
        {tally.map((item) => (
          <AttendanceTallyTile
            key={item.id}
            value={item.count}
            caption={ATTENDANCE_TILE_LABEL[item.id]}
            {...ATTENDANCE_TILE_COLORS[item.id]}
          />
        ))}
      </Flex>

      <Box sx={{ flexShrink: 0, px: 1.5, pt: 1.5 }}>
        <SearchInput
          searchKey={searchKey}
          onSearch={onSearch}
          onClear={() => onSearch("")}
          placeholder={SESSION_NOTE_LABEL.searchPlaceholder}
          name="participantSearch"
          startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
        />
      </Box>

      {/* minHeight lets the roster scroll without pushing the tracker away. */}
      <Flex
        flexDirection="column"
        gap={1}
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.5, pt: 1.5 }}
      >
        {participants.map((participant) => (
          <AttendanceRow
            key={participant.uuid}
            participant={participant}
            onStatusChange={onStatusChange}
            isSelected={participant.uuid === selectedUuid}
            onSelect={onSelect}
            onShowSummary={onShowSummary}
            disabled={disabled}
          />
        ))}
      </Flex>

      <Flex
        flexDirection="column"
        gap={0.75}
        sx={{
          flexShrink: 0,
          px: 1.5,
          py: 1.5,
          borderTop: `1px solid ${CHART_COLORS.borderLight}`,
        }}
      >
        <Flex alignItems="center" justifyContent="space-between" gap={1}>
          <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
            {SESSION_NOTE_LABEL.completionTracker}
          </Text>
          <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textDarker}>
            {`${completedNotes}/${totalParticipants} ${SESSION_NOTE_LABEL.completionGroupSuffix}`}
          </Text>
        </Flex>

        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label={SESSION_NOTE_LABEL.completionTracker}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: CHART_COLORS.borderLight,
            "& .MuiLinearProgress-bar": {
              backgroundColor: CHART_COLORS.btnPrimaryBg,
            },
          }}
        />
      </Flex>
    </Flex>
  );
};

export default AttendancePanel;
